import { useQuery } from "@tanstack/react-query";
import { callWithAuth, getCardCountV2 } from "@/api/Api";
import { REVIEW_CARD_COUNT_QUERY_KEY } from "@/components/Review/ReviewInsights";
import { getDailyReviewBadgeCount } from "@/components/Review/dailyReviewBadge";
import { DAILY_REVIEW_BADGE_THRESHOLD } from "@/components/Review/reviewConstants";
import { useReviewCooldownActive } from "@/components/Review/reviewCooldown";

/**
 * useReviewCount — web port of client-native/src/hooks/useReviewCount.ts.
 *
 * Returns the number of review cards due (words + phrases) via the v2 card
 * count endpoint, feeding the numeric badge on the Dictionary tab.
 *
 * Queries the SAME key the swipe mutations invalidate
 * (REVIEW_CARD_COUNT_QUERY_KEY, =`reviewCardsStats`), matching iOS — so the
 * badge refreshes after a swipe (right/left/delete in useReviewCards).
 *
 * iOS additionally applies a daily-badge dampener (`getBadgeEligibleReviewCount`)
 * that floors the badge to 0 once the user reviews enough today, plus a review
 * cooldown store (`globalStore.reviewCooldownEndTime`) that zeroes the badge for
 * a window after a session exits. We port BOTH: the daily-threshold dampener
 * (reading the local count from `recordDailyReviewBadgeCompletions`) and the
 * cooldown (`useReviewCooldownActive`, the web equivalent of iOS's reactive
 * `useValue(globalStore.reviewCooldownEndTime)`).
 *
 * how to verify: seed a user with review cards (`parrot test-data`), load the
 * shell, and confirm the Dictionary tab shows the count; swipe through cards and
 * confirm the badge floors to 0 after `DAILY_REVIEW_BADGE_THRESHOLD` reviews.
 */
export function useReviewCount(): number {
  // Subscribe to cooldown state for reactivity (iOS: useValue(reviewCooldownEndTime)).
  const isCooldownActive = useReviewCooldownActive();

  const { data } = useQuery({
    queryKey: [REVIEW_CARD_COUNT_QUERY_KEY],
    queryFn: () => callWithAuth(getCardCountV2),
    retry: 5,
    retryDelay: () => 200,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const serverReviewCount = data?.count ?? 0;

  // Cooldown dampener (iOS isReviewCooldownActive): for a window after exiting a
  // session, show "done" regardless of the server count.
  if (isCooldownActive) {
    return 0;
  }

  // Daily dampener (iOS getBadgeEligibleReviewCount): once the user has reviewed
  // enough cards today, suppress the badge regardless of the server count.
  if (getDailyReviewBadgeCount() >= DAILY_REVIEW_BADGE_THRESHOLD) {
    return 0;
  }

  return serverReviewCount;
}
